import { FilesUploader, If, VideoThumbnailSelector } from "components";
import { MediaDirectoryKeys, MediaType } from "constants/enums";
import {
  generateVideoThumbnails,
  VideoThumbnail
} from "helpers/generateVideoThumbnails";
import { useDirectUploadToS3 } from "libs/directUpload";
import { useState, useEffect } from "react";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { FormField, MultipleMediaFilesPicker, RadioButton } from "ui";

import { ProductVideoVariant } from "../../../../constants/enums";
import { ProductFormValues } from "../ProductDetailsForm.types";

import styles from "../ProductDetailsForm.module.scss";

const PRODUCT_VIDEO_SIZE = {
  width: 176,
  height: 248
};

export const ProductVideoBlock = () => {
  const { t } = useTranslation();
  const { control, register, watch, setValue } =
    useFormContext<ProductFormValues>();
  const { uploader } = useDirectUploadToS3();

  const {
    fields: mediaFiles,
    append: appendMediaFile,
    remove: removeMediaFile
  } = useFieldArray({
    control,
    name: "generalVideo"
  });

  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [thumbnails, setThumbnails] = useState<VideoThumbnail[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const productVideoVariant = watch("productVideoVariant");
  const generalVideo = watch("generalVideo");

  const hasVideo = generalVideo && generalVideo.length > 0;

  useEffect(() => {
    if (!videoFile) {
      setThumbnails([]);
      setSelectedIndex(0);
      return;
    }

    const generate = async () => {
      setIsGenerating(true);
      try {
        const generated = await generateVideoThumbnails(
          videoFile,
          PRODUCT_VIDEO_SIZE
        );
        setThumbnails(generated);
        setSelectedIndex(0);

        if (generated.length > 0) {
          await uploadThumbnail(generated[0]);
        }
      } catch {
        toast.error(t("Failed to generate thumbnails"));
      } finally {
        setIsGenerating(false);
      }
    };

    generate();
  }, [videoFile]);

  const uploadThumbnail = async (thumbnail: VideoThumbnail) => {
    setIsUploading(true);
    try {
      const file = new File([thumbnail.blob], "thumbnail.jpg", {
        type: "image/jpeg"
      });

      const fileWithMetadata = Object.assign(file, {
        width: PRODUCT_VIDEO_SIZE.width,
        height: PRODUCT_VIDEO_SIZE.height
      });

      const media = await uploader(fileWithMetadata, {
        directoryKey: MediaDirectoryKeys.ProductVariant,
        onError: () => {
          toast.error(t("Failed to upload thumbnail"));
        }
      });

      if (media) {
        setValue("generalVideoThumbnail", media);
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleSelectThumbnail = async (newIndex: number) => {
    if (newIndex === selectedIndex || !thumbnails[newIndex]) return;

    setSelectedIndex(newIndex);
    await uploadThumbnail(thumbnails[newIndex]);
  };

  const handleVideoFileReceived = (file: File) => {
    setVideoFile(file);
  };

  const handleRemoveVideo = (index: number) => {
    setVideoFile(null);
    setThumbnails([]);
    setSelectedIndex(0);
    setValue("generalVideoThumbnail", null);
    removeMediaFile(index);
    setValue("productVideoVariant", ProductVideoVariant.NO_VIDEO);
  };

  return (
    <FormField label={t("Product video")}>
      <div className={styles.productForm__radios}>
        <RadioButton
          label={t("No video")}
          {...register("productVideoVariant")}
          value={ProductVideoVariant.NO_VIDEO}
        />
        <RadioButton
          label={t("One main video")}
          {...register("productVideoVariant")}
          value={ProductVideoVariant.ONE_MAIN_VIDEO}
        />
        <RadioButton
          label={t("Video for each variant")}
          {...register("productVideoVariant")}
          value={ProductVideoVariant.FOR_EACH_VIDEO}
        />
      </div>

      <If
        condition={productVideoVariant === ProductVideoVariant.ONE_MAIN_VIDEO}
      >
        <Controller
          control={control}
          name="generalVideo"
          render={({ fieldState: { error } }) => (
            <FormField errorMessage={error?.message}>
              <br />
              <FilesUploader
                onSuccessUploading={appendMediaFile}
                uploadDirectoryKey={MediaDirectoryKeys.ProductVariant}
                onVideoFileReceived={handleVideoFileReceived}
              >
                {(uploaderProps) => (
                  <MultipleMediaFilesPicker
                    label={t("Upload main video (176:248)")}
                    allowedMediaTypes={[MediaType.VIDEO]}
                    size="lg"
                    {...uploaderProps}
                    value={mediaFiles}
                    filesLimit={1}
                    onDeleteMediaFile={handleRemoveVideo}
                  />
                )}
              </FilesUploader>

              <If condition={hasVideo}>
                <VideoThumbnailSelector
                  thumbnails={thumbnails}
                  selectedIndex={selectedIndex}
                  onSelect={handleSelectThumbnail}
                  isLoading={isGenerating || isUploading}
                  aspectRatio={
                    PRODUCT_VIDEO_SIZE.width / PRODUCT_VIDEO_SIZE.height
                  }
                />
              </If>
            </FormField>
          )}
        />
      </If>
    </FormField>
  );
};
